import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  useTheme,
  Tooltip,
  Checkbox,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoreVert as MoreVertIcon,
  GetApp as ExportIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowEditorSimple from "../../components/FlowBuilder/FlowEditorSimple";
import FlowEditorReactFlow from "../../components/FlowBuilder/FlowEditorReactFlow";
import NewFlowModal from "../../components/FlowBuilder/NewFlowModal";
import ImportTypebotModal from "../../components/FlowBuilder/ImportTypebotModal";
import { i18n } from "../../translate/i18n";
import {
  listFlowBuilders,
  getFlowBuilder,
  createFlowBuilder,
  saveFlowDraft,
  listFlowAlerts,
  exportFlows,
  discardFlowDraft,
  deleteFlowBuilder,
} from "../../services/flowBuilders.api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTimezone } from "../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    padding: theme.spacing(1, 2),
  },
}));

const FlowBuilders = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  // eslint-disable-next-line no-unused-vars
  const { user } = useContext(AuthContext);
  const { formatDate } = useTimezone();

  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // 0434 L1: el total lo da el servidor (antes era flows.length y la tabla mostraba todo).
  const [total, setTotal] = useState(0);
  // Todos / activos / inactivos. Antes solo se pedían los activos: un flujo desactivado
  // desaparecía de la lista sin forma de volver a verlo.
  const [estadoFiltro, setEstadoFiltro] = useState("todos");
  const [textoBusqueda, setTextoBusqueda] = useState("");
  // Exportar (0434 X2): flujos marcados, de cualquier página.
  const [seleccion, setSeleccion] = useState([]);
  const [exportando, setExportando] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  // Nodos webhook/IA con fallos seguidos, por flujo (0422 R5)
  const [alertasPorFlujo, setAlertasPorFlujo] = useState({});
  const [deletingFlow, setDeletingFlow] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const [newFlowModalOpen, setNewFlowModalOpen] = useState(false);
  const isMountedRef = useRef(true);

  // ✅ Cleanup para prevenir actualizaciones en componente desmontado
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ✅ Si hay ID en la ruta, mostrar editor
  const currentPath = location.pathname;
  const isEditor = id !== undefined || currentPath === "/flowbuilders/new" || (currentPath.includes("/flowbuilders/") && currentPath.includes("/edit"));
  const isNewFlow = id === "new" || currentPath === "/flowbuilders/new";

  useEffect(() => {
    const path = location.pathname;
    const isEditorPath = path === "/flowbuilders/new" || (path.includes("/flowbuilders/") && path.includes("/edit"));
    const isNewPath = path === "/flowbuilders/new";
    
    if (isEditorPath && !isNewPath && id) {
      fetchFlowById();
    } else if (isNewPath) {
      setCurrentFlow({ name: "", flow: { nodes: [], edges: [] } });
    } else if (!isEditorPath) {
      fetchFlows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, id, searchParam, page, rowsPerPage, estadoFiltro]);

  // Búsqueda con una espera breve: sin ella, cada tecla era una consulta.
  useEffect(() => {
    const t = setTimeout(() => {
      if (textoBusqueda !== searchParam) {
        setSearchParam(textoBusqueda);
        setPage(0);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textoBusqueda]);

  const fetchFlowById = async () => {
    try {
      if (isMountedRef.current) {
        setLoadingFlow(true);
      }
      const { data } = await getFlowBuilder(id);
      if (isMountedRef.current) {
        // Borrador y publicación (0422 R4): si hay un borrador sin publicar, el editor abre ESE.
        setCurrentFlow({ ...data, flow: data.draftFlow || data.flow, hayBorrador: Boolean(data.draftFlow) });
      }
    } catch (err) {
      if (isMountedRef.current) {
        toastError(err);
        history.push("/flowbuilders");
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingFlow(false);
      }
    }
  };

  const fetchFlows = async () => {
    try {
      if (isMountedRef.current) {
        setLoading(true);
      }
      const { data } = await listFlowBuilders({
        searchParam: searchParam || "",
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        ...(estadoFiltro === "todos" ? {} : { active: estadoFiltro === "activos" }),
      });

      if (!isMountedRef.current) return;

      // ✅ Validar y mapear datos recibidos
      const flowsData = data.records || [];
      
      // ✅ Asegurar que todos los registros tengan ID válido y datos correctos
      const validFlows = flowsData.map((flow) => ({
        ...flow,
        id: flow?.id || flow?.Id || 0,
        name: flow?.name || flow?.Name || 'Sin nombre',
        active: flow?.active !== undefined ? flow.active : flow?.Active !== undefined ? flow.Active : false,
        createdAt: flow?.createdAt || flow?.CreatedAt || new Date()
      }));
      
      if (isMountedRef.current) {
        const totalServidor = Number(data.count) || 0;
        // Si se borró el último flujo de la última página, volver a la anterior.
        if (validFlows.length === 0 && page > 0 && totalServidor > 0) {
          setPage(Math.max(0, Math.ceil(totalServidor / rowsPerPage) - 1));
          return;
        }
        setFlows(validFlows);
        setTotal(totalServidor);
      }
      // La salud no debe impedir ver la lista: si falla, simplemente no se marca nada.
      listFlowAlerts()
        .then(({ data: d }) => {
          if (!isMountedRef.current) return;
          const porFlujo = {};
          for (const a of d?.alerts || []) (porFlujo[a.flowId] = porFlujo[a.flowId] || []).push(a);
          setAlertasPorFlujo(porFlujo);
        })
        .catch(() => {});
    } catch (err) {
      if (isMountedRef.current) {
        toastError(err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  /**
   * Descarga los flujos marcados como un .json para importarlo en otra instalación. El backend
   * añade solo los flujos a los que saltan, y nunca incluye credenciales.
   */
  const handleExport = async (ids) => {
    setExportando(true);
    try {
      const { data } = await exportFlows(ids);
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const fecha = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = data.flujos.length === 1
        ? `flujo-${data.flujos[0].nombre.replace(/[^\w\-]+/g, "-").replace(/^-+|-+$/g, "")}-${fecha}.json`
        : `flujos-${data.flujos.length}-${fecha}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const extra = data.flujos.length - ids.length;
      toast.success(
        `Exportado${data.flujos.length === 1 ? "" : "s"} ${data.flujos.length} flujo${data.flujos.length === 1 ? "" : "s"}` +
          (extra > 0 ? ` (incluye ${extra} al${extra === 1 ? "" : "os"} que saltan)` : "")
      );
      if (data.avisos?.length) {
        toast.warn(`Revisa en el destino: ${data.avisos.map((a) => a.mensaje).join(" · ")}`, { autoClose: 15000 });
      }
      setSeleccion([]);
    } catch (err) {
      toastError(err);
    } finally {
      setExportando(false);
    }
  };

  const handleSearch = (event) => {
    setTextoBusqueda(event.target.value);
  };

  const handleNewFlow = () => {
    // El flujo nace con su provider (0371 WF0): pedimos nombre + canal antes de abrir el editor.
    setNewFlowModalOpen(true);
  };

  const handleCreateFlow = async ({ name, provider }) => {
    try {
      const { data } = await createFlowBuilder({
        name,
        provider,
        flow: { nodes: [], edges: [] },
        active: true,
      });
      setNewFlowModalOpen(false);
      if (data?.id) {
        history.push(`/flowbuilders/${data.id}/edit`);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleEditFlow = (flow) => {
    if (!flow || !flow.id) {
      toast.error("Error: No se pudo obtener el ID del flujo");
      return;
    }
    history.push(`/flowbuilders/${flow.id}/edit`);
  };

  const handleDeleteFlow = async () => {
    if (!deletingFlow || !deletingFlow.id) {
      toast.error("Error: No se pudo obtener el ID del flujo");
      return;
    }

    try {
      await deleteFlowBuilder(deletingFlow.id);
      toast.success(i18n.t("flowbuilders.toasts.deleted"));
      fetchFlows();
      setConfirmModalOpen(false);
      setDeletingFlow(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleMenuOpen = (event, flow) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedFlow(flow);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedFlow(null);
  };

  const handleMenuEdit = () => {
    if (selectedFlow) {
      handleEditFlow(selectedFlow);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (selectedFlow) {
      setDeletingFlow(selectedFlow);
      setConfirmModalOpen(true);
    }
    handleMenuClose();
  };

  const handleSaveFlow = async (flowData, isAutoSave = false) => {
    try {
      if (isNewFlow) {
        // Crear nuevo flujo
        const { data } = await createFlowBuilder({
          name: flowData.name || "Nuevo Flujo",
          flow: flowData.flow,
          active: true,
        });
        // ✅ NO redirigir - el usuario permanece en el editor
        // Solo actualizar el ID si es necesario
        if (data?.id) {
          // Actualizar la URL sin recargar
          window.history.replaceState({}, "", `/flowbuilders/${data.id}/edit`);
        }
      } else {
        // Actualizar flujo existente
        // Guardar deja un BORRADOR (0422 R4): las conversaciones siguen con lo publicado hasta
        // que se pulse «Publicar». Si solo cambió la disposición, el backend la escribe directa.
        const { data } = await saveFlowDraft(id, {
          name: flowData.name,
          flow: flowData.flow,
        });
        return data;
        // ✅ NO redirigir - el usuario permanece en el editor
      }
    } catch (err) {
      toastError(err);
      throw err;
    }
  };

  const handleDiscardDraft = async () => {
    await discardFlowDraft(id);
    await fetchFlowById();
  };

  const handleCancelEditor = () => {
    history.push("/flowbuilders");
  };

  // ✅ Si es editor, mostrar el editor visual
  const path = location.pathname;
  const shouldShowEditor = path === "/flowbuilders/new" || (path.includes("/flowbuilders/") && path.includes("/edit"));
  const isNewPath = path === "/flowbuilders/new";
  
  if (shouldShowEditor) {
    if (loadingFlow && !isNewPath && id) {
      return (
        <MainContainer>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography>{i18n.t("flowbuilders.loading")}</Typography>
          </Box>
        </MainContainer>
      );
    }

    return (
      <MainContainer>
        <FlowEditorReactFlow
          flowId={isNewPath ? null : id}
          initialFlow={currentFlow || (isNewPath ? { name: "", flow: { nodes: [], edges: [] } } : null)}
          onSave={handleSaveFlow}
          onCancel={handleCancelEditor}
          onDiscardDraft={isNewPath ? null : handleDiscardDraft}
        />
      </MainContainer>
    );
  }

  // ✅ Si no es editor, mostrar lista
  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("flowbuilders.title")}</Title>
        <MainHeaderButtonsWrapper>
          {/* Importar desde Typebot (0422 T1): el backend exige settings:administrar */}
          {user?.profile === "admin" && (
            <Button variant="outlined" color="primary" onClick={() => setImportModalOpen(true)}>
              Importar
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewFlow}
            startIcon={<AddIcon />}
          >
            {i18n.t("flowbuilders.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {seleccion.length > 0 ? (
          <Box display="flex" alignItems="center" p={2} style={{ gap: 12, minHeight: 72 }}>
            <Typography variant="subtitle1">
              {seleccion.length} flujo{seleccion.length === 1 ? "" : "s"} seleccionado{seleccion.length === 1 ? "" : "s"}
            </Typography>
            <Button variant="contained" color="primary" startIcon={<ExportIcon />} disabled={exportando} onClick={() => handleExport(seleccion)}>
              {exportando ? "Exportando…" : "Exportar"}
            </Button>
            <Button onClick={() => setSeleccion([])} disabled={exportando}>Cancelar</Button>
          </Box>
        ) : (
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <TextField
            placeholder={i18n.t("flowbuilders.searchPlaceholder")}
            type="search"
            size="small"
            variant="outlined"
            value={textoBusqueda}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            variant="outlined"
            label="Estado"
            value={estadoFiltro}
            onChange={(e) => { setEstadoFiltro(e.target.value); setPage(0); }}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="activos">Activos</MenuItem>
            <MenuItem value="inactivos">Inactivos</MenuItem>
          </TextField>
        </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  color="primary"
                  indeterminate={flows.some((f) => seleccion.includes(f.id)) && !flows.every((f) => seleccion.includes(f.id))}
                  checked={flows.length > 0 && flows.every((f) => seleccion.includes(f.id))}
                  onChange={(e) => {
                    const ids = flows.map((f) => f.id);
                    setSeleccion((p) => (e.target.checked ? [...new Set([...p, ...ids])] : p.filter((x) => !ids.includes(x))));
                  }}
                  inputProps={{ "aria-label": "Seleccionar los flujos de esta página" }}
                />
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.name")}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.status")}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.createdAt")}
              </TableCell>
              <TableCell className={classes.customTableCell} align="center">
                {i18n.t("flowbuilders.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={5} />}
            {!loading && flows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchParam || estadoFiltro !== "todos"
                      ? "Ningún flujo coincide con la búsqueda o el filtro."
                      : i18n.t("flowbuilders.noFlows")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              flows.map((flow, index) => (
                <TableRow key={flow?.id || `flow-${index}`} hover selected={seleccion.includes(flow?.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={seleccion.includes(flow?.id)}
                      onChange={(e) => setSeleccion((p) => (e.target.checked ? [...p, flow.id] : p.filter((x) => x !== flow.id)))}
                      inputProps={{ "aria-label": `Seleccionar ${flow?.name}` }}
                    />
                  </TableCell>
                  <TableCell 
                    className={classes.customTableCell}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEditFlow(flow)}
                  >
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontWeight: 500,
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        transition: 'text-decoration-color 0.2s',
                        '&:hover': {
                          textDecorationColor: theme.palette.primary.main,
                        }
                      }}
                    >
                      {flow?.name || 'Sin nombre'}
                    </Typography>
                    {flow?.hasDraft && (
                      <Tooltip title="Hay cambios guardados que las conversaciones aún no usan: ábrelo y pulsa Publicar">
                        <Chip label="Borrador sin publicar" size="small" style={{ marginTop: 4, backgroundColor: "#1e88e5", color: "#fff" }} />
                      </Tooltip>
                    )}
                    {alertasPorFlujo[flow?.id] && (
                      <Tooltip
                        title={alertasPorFlujo[flow.id]
                          .map((a) => `${a.kind === "openai" ? "IA" : "Webhook"} (${a.nodeId}): ${a.failCount} fallos seguidos — ${a.lastError || ""}`)
                          .join(" · ")}
                      >
                        <Chip
                          label={`⚠ ${alertasPorFlujo[flow.id].some((a) => a.kind === "openai") ? "IA" : "Webhook"} fallando`}
                          size="small"
                          style={{ marginTop: 4, marginLeft: 4, backgroundColor: "#e53935", color: "#fff" }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    <Chip
                      label={flow?.active ? i18n.t("flowbuilders.status.active") : i18n.t("flowbuilders.status.inactive")}
                      color={flow?.active ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    {flow?.createdAt ? formatDate(flow.createdAt) : 'N/A'}
                  </TableCell>
                  <TableCell className={classes.customTableCell} align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditFlow(flow)}
                        style={{ color: theme.palette.primary.main }}
                        title={i18n.t("flowbuilders.actions.edit") || "Editar"}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleExport([flow.id])}
                        disabled={exportando}
                        title="Exportar este flujo"
                      >
                        <ExportIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingFlow(flow);
                          setConfirmModalOpen(true);
                        }}
                        style={{ color: theme.palette.error.main }}
                        title={i18n.t("flowbuilders.actions.delete") || "Eliminar"}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          labelRowsPerPage={i18n.t("flowbuilders.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuEdit}>
            <EditIcon style={{ marginRight: 8, fontSize: 18 }} />
            {i18n.t("flowbuilders.actions.edit")}
          </MenuItem>
          <MenuItem onClick={handleMenuDelete}>
            <DeleteOutlineIcon style={{ marginRight: 8, fontSize: 18 }} />
            {i18n.t("flowbuilders.actions.delete")}
          </MenuItem>
        </Menu>
        <ConfirmationModal
          title={i18n.t("flowbuilders.confirmationModal.deleteTitle")}
          open={confirmModalOpen}
          onClose={() => {
            setConfirmModalOpen(false);
            setDeletingFlow(null);
          }}
          onConfirm={handleDeleteFlow}
        >
          {i18n.t("flowbuilders.confirmationModal.deleteMessage")}
        </ConfirmationModal>
        <NewFlowModal
          open={newFlowModalOpen}
          onClose={() => setNewFlowModalOpen(false)}
          onConfirm={handleCreateFlow}
        />
        <ImportTypebotModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImported={fetchFlows}
        />
      </Paper>
    </MainContainer>
  );
};

export default FlowBuilders;
